import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ReachMap from "@/components/ReachMap";

const EMPTY = { city: "", country: "", region: "India", x: 70, y: 50, lat: "", lng: "" };
const REGIONS = ["India", "International"];

export default function ReachManager() {
  const [locations, setLocations] = useState([]);
  const [edits, setEdits] = useState({});
  const [creating, setCreating] = useState(EMPTY);
  const [savingId, setSavingId] = useState(null);
  const dragIndex = useRef(null);

  const load = async () => {
    const { data } = await api.get("/reach");
    setLocations(data || []);
  };
  useEffect(() => { load(); }, []);

  const val = (l, k) => (edits[l.id]?.[k] !== undefined ? edits[l.id][k] : l[k]);
  const setField = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [k]: v } }));

  const numOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const saveOne = async (l) => {
    setSavingId(l.id);
    try {
      await api.put(`/reach/${l.id}`, {
        city: val(l, "city"),
        country: val(l, "country") || "",
        region: val(l, "region"),
        x: parseFloat(val(l, "x")) || 0,
        y: parseFloat(val(l, "y")) || 0,
        lat: numOrNull(val(l, "lat")),
        lng: numOrNull(val(l, "lng")),
      });
      toast.success("Saved.");
      setEdits((e) => { const n = { ...e }; delete n[l.id]; return n; });
      load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this location?")) return;
    try {
      await api.delete(`/reach/${id}`);
      toast.success("Removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const add = async () => {
    if (!creating.city.trim()) {
      toast.error("City name is required.");
      return;
    }
    try {
      await api.post("/reach", {
        ...creating,
        x: parseFloat(creating.x) || 0,
        y: parseFloat(creating.y) || 0,
        lat: numOrNull(creating.lat),
        lng: numOrNull(creating.lng),
      });
      toast.success("Location added.");
      setCreating(EMPTY);
      load();
    } catch {
      toast.error("Failed to add.");
    }
  };

  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragEnter = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setLocations((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
  };
  const onDragEnd = async () => {
    dragIndex.current = null;
    try {
      await api.put("/reach/reorder", { ids: locations.map((l) => l.id) });
      toast.success("Order saved.");
    } catch {
      toast.error("Failed to save order.");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reach-manager">
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8] mb-1">Home page</p>
        <h2 className="font-display font-semibold text-xl text-[#334155] mb-1">Reach locations (Home &amp; About maps)</h2>
        <p className="text-sm text-[#52525B]">
          Add, edit or delete the dots shown on the &quot;Our Reach&quot; map. Use real
          <b> Latitude</b> and <b>Longitude</b> for accurate placement (e.g. Mumbai: 19.076, 72.877).
          The legacy X/Y percentages are still stored as a fallback for older rows.
        </p>
      </div>

      {/* Preview map */}
      <div className="border border-black/10 bg-[#F0F5F9] p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#78be27]">Live preview</p>
          <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">{locations.length} location(s)</p>
        </div>
        <div className="border border-black/10 bg-white overflow-hidden">
          <ReachMap locations={locations} />
        </div>
      </div>

      {/* Add new */}
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#78be27] mb-4">Add a location</p>
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">City *</Label>
            <Input
              value={creating.city}
              onChange={(e) => setCreating({ ...creating, city: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="e.g. Chennai"
              data-testid="admin-reach-new-city"
            />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Country</Label>
            <Input
              value={creating.country}
              onChange={(e) => setCreating({ ...creating, country: e.target.value })}
              className="mt-1 rounded-none border-black/15"
              placeholder="India"
              data-testid="admin-reach-new-country"
            />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Region</Label>
            <Select value={creating.region} onValueChange={(v) => setCreating({ ...creating, region: v })}>
              <SelectTrigger className="mt-1 rounded-none border-black/15" data-testid="admin-reach-new-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Latitude</Label>
            <Input
              type="number" step="0.0001"
              value={creating.lat}
              onChange={(e) => setCreating({ ...creating, lat: e.target.value })}
              className="mt-1 rounded-none border-black/15 font-mono-accent"
              placeholder="e.g. 19.076"
              data-testid="admin-reach-new-lat"
            />
          </div>
          <div>
            <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Longitude</Label>
            <Input
              type="number" step="0.0001"
              value={creating.lng}
              onChange={(e) => setCreating({ ...creating, lng: e.target.value })}
              className="mt-1 rounded-none border-black/15 font-mono-accent"
              placeholder="e.g. 72.877"
              data-testid="admin-reach-new-lng"
            />
          </div>
          <div className="md:col-span-6">
            <button
              type="button"
              onClick={add}
              data-testid="admin-reach-add-btn"
              className="w-full bg-[#3da8d8] text-white h-11 flex items-center justify-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest hover:bg-[#3a82ba]"
            >
              <Plus size={14} /> Add Location
            </button>
          </div>
        </div>
      </div>

      {/* Existing */}
      <div className="border border-black/10 bg-white p-6">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8] mb-4">Existing — drag to reorder</p>
        <div className="space-y-3">
          {locations.map((l, i) => {
            const dirty = !!edits[l.id];
            return (
              <div
                key={l.id}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragEnter={() => onDragEnter(i)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={onDragEnd}
                data-testid={`admin-reach-row-${l.id}`}
                className={`grid grid-cols-[auto_1fr_1fr_1fr_auto_auto_auto] items-center gap-3 border p-3 bg-white ${dirty ? "border-[#78be27]" : "border-black/10"}`}
              >
                <GripVertical size={16} className="text-[#94A3B8] cursor-grab active:cursor-grabbing shrink-0" />
                <Input
                  value={val(l, "city")}
                  onChange={(e) => setField(l.id, "city", e.target.value)}
                  className="rounded-none border-black/15"
                  placeholder="City"
                  data-testid={`admin-reach-city-${l.id}`}
                />
                <Input
                  value={val(l, "country") || ""}
                  onChange={(e) => setField(l.id, "country", e.target.value)}
                  className="rounded-none border-black/15"
                  placeholder="Country"
                  data-testid={`admin-reach-country-${l.id}`}
                />
                <Select value={val(l, "region")} onValueChange={(v) => setField(l.id, "region", v)}>
                  <SelectTrigger className="rounded-none border-black/15" data-testid={`admin-reach-region-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  type="number" step="0.0001"
                  value={val(l, "lat") ?? ""}
                  onChange={(e) => setField(l.id, "lat", e.target.value)}
                  className="w-24 rounded-none border-black/15 font-mono-accent text-xs"
                  placeholder="lat"
                  data-testid={`admin-reach-lat-${l.id}`}
                />
                <Input
                  type="number" step="0.0001"
                  value={val(l, "lng") ?? ""}
                  onChange={(e) => setField(l.id, "lng", e.target.value)}
                  className="w-24 rounded-none border-black/15 font-mono-accent text-xs"
                  placeholder="lng"
                  data-testid={`admin-reach-lng-${l.id}`}
                />
                <div className="flex items-center gap-1">
                  {dirty && (
                    <button
                      type="button"
                      onClick={() => saveOne(l)}
                      disabled={savingId === l.id}
                      data-testid={`admin-reach-save-${l.id}`}
                      className="w-9 h-9 flex items-center justify-center bg-[#78be27] text-white hover:bg-[#6aa81f] disabled:opacity-50"
                    >
                      <Save size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(l.id)}
                    data-testid={`admin-reach-delete-${l.id}`}
                    className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#52525B] hover:text-red-600 hover:border-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {locations.length === 0 && (
            <p className="text-sm text-[#94A3B8] italic">No locations yet — add one above.</p>
          )}
        </div>
      </div>
    </div>
  );
}
